import { useState } from 'react';
import produce from 'immer';
import useBoundStore from 'store';
import { Writable } from 'ts-essentials';
import { useLocation } from 'react-router-dom';
import { complainAboutApartment, createCallRequest } from 'api';
import { formatApartmentDescAlternative, userHasAccess } from 'lib/utils';
import { APARTMENT_RESERVE_TIME, COMPLAINT_REASONS_MAP } from 'lib/constants';
import { ApartmentNavigateProps } from 'screens/apartment';
import Header from 'components/header';
import Button from 'components/button';
import Checkbox from 'components/checkbox';
import BottomSheetItem from 'components/bottom-sheet-item';
import ShareItemContainer from 'components/share-item-container';
import BottomSheet, { BottomSheetProps } from 'components/bottom-sheet';
import BottomSheetItemsContainer from 'components/bottom-sheet-items-container';
import placeholder from 'assets/images/placeholder.png';
import styles from './styles.module.scss';
import BottomSheetResult from '../bottom-sheet-result';
import useApartment from 'api/hooks/use-apartment';
import { MarketTypes } from 'api/types';
import ModalRansomInSystem from 'screens/estimate/components/modals/modalAnalogs/inSystem/modalRansom';
import { toast } from 'react-toastify';
import ModalRedirectToPageWizardDealService from 'screens/estimate/components/modals/modalAnalogs/modalRedirectToPageWizardDealService';

type ApartmentActionBottomSheetProps = Pick<BottomSheetProps, 'open' | 'onDismiss'>;

enum ApartmentActionBottomSheetState {
  default,
  complaining,
  complained,
  sharing,
  tradeIn,
  reserveSuccessful,
  other,
  subsiding,
  deal,
  ransom,
}

const useApartmentWithLocation = () => {
  const apartmentId = (useLocation().state as ApartmentNavigateProps).id;
  const apartment = useApartment(apartmentId).apartment!;
  return { apartmentId, apartment };
};

const SecondaryApartmentActions: React.FC<ApartmentActionBottomSheetProps> = (props) => {
  const { onDismiss, ...rest } = props;
  const user = useBoundStore((s) => s.user);
  const { apartmentId } = useApartmentWithLocation();
  const [state, setState] = useState(ApartmentActionBottomSheetState.default);

  const handleDismiss = () => {
    onDismiss && onDismiss();
    setTimeout(() => {
      setState(ApartmentActionBottomSheetState.default);
    }, 500);
  };

  const isHideForStaff = !userHasAccess(
    [
      'Manager',
      'Director',
      'Creator',
      'Lawyer',
      'Accountant',
      'Registrant',
      'Operator',
      'Logistician',
    ],
    user?.employeeRoles,
  );

  const handleOpenComplaint = () => setState(ApartmentActionBottomSheetState.complaining);

  const forceAuthorize = useBoundStore((s) => s.forceAuthorize);

  const handleInTradeIn = async () => {
    if (forceAuthorize()) {
      setState(ApartmentActionBottomSheetState.tradeIn);
    }
  };

  const handleRansom = async () => {
    if (forceAuthorize()) {
      setState(ApartmentActionBottomSheetState.ransom);
    }
  };

  const handleOther = async () => {
    if (forceAuthorize()) {
      setState(ApartmentActionBottomSheetState.other);
    }
  };

  const handleSubsiding = async () => {
    if (forceAuthorize()) {
      setState(ApartmentActionBottomSheetState.subsiding);
    }
  };

  const handleDeal = async () => {
    if (forceAuthorize()) {
      setState(ApartmentActionBottomSheetState.deal);
    }
  };

  const handleCompleteComplaint = async (reasons: any) => {
    const res = await complainAboutApartment(apartmentId, reasons);
    if (res.result) {
      setState(ApartmentActionBottomSheetState.complained);
    }
  };

  const header =
    state === ApartmentActionBottomSheetState.default ? undefined : state ===
      ApartmentActionBottomSheetState.complaining ? (
      <Header className={styles.header} text='Причины жалобы' />
    ) : null;

  const ModalView = (function () {
    switch (state) {
      case ApartmentActionBottomSheetState.ransom:
        return (
          <ModalRansomInSystem
            id={apartmentId}
            onDismiss={handleDismiss}
            isOpen={props.open}
            marketType={MarketTypes.SECONDARY}
          />
        );

      case ApartmentActionBottomSheetState.deal:
        return (
          <ModalRedirectToPageWizardDealService
            isOpen={props.open}
            onDismiss={handleDismiss}
            data={{}}
          />
        );
    }
  })();

  const body = !ModalView
    ? ((): React.ReactNode => {
        switch (state) {
          case ApartmentActionBottomSheetState.complaining:
            return <Complaining handleComplaint={handleCompleteComplaint} />;

          case ApartmentActionBottomSheetState.complained:
            return <ComplaintSuccessful />;

          case ApartmentActionBottomSheetState.tradeIn:
            return <TradeIn onClose={handleDismiss} />;

          case ApartmentActionBottomSheetState.sharing:
            return <Sharing />;

          case ApartmentActionBottomSheetState.reserveSuccessful:
            return <ReserveSuccessful />;

          case ApartmentActionBottomSheetState.other:
            return (
              <Other
                onComplain={handleOpenComplaint}
                onDeal={handleDeal}
                onSubsiding={handleSubsiding}
              />
            );

          default:
            return (
              <BottomSheetItemsContainer>
                <BottomSheetItem title='Продать Reit' onClick={handleRansom} />
                <BottomSheetItem title='Trade-in' onClick={handleInTradeIn} />
                <BottomSheetItem title='Другое' onClick={handleOther} />
              </BottomSheetItemsContainer>
            );
        }
      })()
    : undefined;

  return (
    <>
      {body ? (
        <BottomSheet {...rest} noScroll onDismiss={handleDismiss} header={header}>
          {body}
        </BottomSheet>
      ) : (
        ModalView
      )}
    </>
  );
};

type ComplainingProps = {
  handleComplaint: (reasons: string[]) => void;
};
type ComplaintReason = (typeof COMPLAINT_REASONS_MAP)[number];

const Complaining: React.FC<ComplainingProps> = (props) => {
  const { handleComplaint } = props;
  const [checked, setChecked] = useState(new Set<ComplaintReason>());

  const handleChange = (reason: Writable<ComplaintReason>) => () => {
    setChecked(
      produce(checked, (checked) => {
        if (checked.has(reason)) {
          checked.delete(reason);
        } else {
          checked.add(reason);
        }
      }),
    );
  };

  const complain = () => {
    handleComplaint([...checked.values()].map((i) => i[1]));
  };

  return (
    <>
      <div className={styles.subSheetComplaintContainer}>
        <div className={styles.subSheetComplaintWrapper}>
          {COMPLAINT_REASONS_MAP.map((r) => (
            <Checkbox
              key={r[0]}
              label={r[0]}
              labelPosition='left'
              checked={checked.has(r)}
              onChange={handleChange(r as Writable<ComplaintReason>)}
              className={styles.complaintCheckbox}
            />
          ))}
        </div>
      </div>

      <Button label='Отправить' onClick={complain} />
    </>
  );
};

const ComplaintSuccessful: React.FC = () => {
  return (
    <BottomSheetResult type='warning' header='Заявка принята'>
      <span className={styles.bold}>
        {'Отправлена жалоба на ' + formatApartmentDescAlternative(1, 10.1, { floor: 1, total: 19 })}
      </span>
    </BottomSheetResult>
  );
};

const Sharing: React.FC = () => (
  <ShareItemContainer
    image={placeholder}
    shareLabel='Отправить объект'
    info={formatApartmentDescAlternative(2, 60.92, { floor: 1, total: 19 })}
    url='https://google.com'
  />
);

const TradeIn = ({ onClose }: { onClose: () => void }) => {
  const handleSend = async () => {
    try {
      await createCallRequest();
      toast.success('Заявка успешно отправлена');
      onClose();
    } catch (error) {
      toast.error('Что то не так, попробуйте позже');
    }
  };

  return (
    <BottomSheetResult type='info' header='Программа trade-in'>
      <Button
        className={styles.wrapperTradeIn}
        label='Оставить заявку на звонок'
        onClick={handleSend}
      />
    </BottomSheetResult>
  );
};

const Other = ({
  onSubsiding,
  onDeal,
  onComplain,
}: {
  onSubsiding: () => void;
  onDeal: () => void;
  onComplain: () => void;
}) => {
  return (
    <BottomSheetResult type='info' header='Другие сервисы Reit'>
      <Button
        className={styles.wrapperTradeIn}
        label='Сервисная сделка от эксперта'
        onClick={onDeal}
      />
      <Button
        className={styles.wrapperTradeIn}
        label='Пожаловаться на объект'
        onClick={onComplain}
        variant='danger'
      />
    </BottomSheetResult>
  );
};

const ReserveSuccessful: React.FC = () => {
  const { apartment } = useApartmentWithLocation();

  return (
    <BottomSheetResult type='clock' header='Заявка на бронирование принята'>
      <span className={styles.bold}>
        {formatApartmentDescAlternative(apartment.rooms, apartment.area, apartment.floor) + ' '}
      </span>
      <span>забронирована на {APARTMENT_RESERVE_TIME} часа</span>
    </BottomSheetResult>
  );
};

export default SecondaryApartmentActions;
